import { useState } from "react";
import {
    Inbox, FileText, FileCheck, Handshake, Hammer, Trophy, Wrench, MapPin, BarChart3,
    ChevronRight, ChevronDown, ArrowRight, Phone, Mail, Clock,
} from "lucide-react";

export const CARD = "bg-white rounded-[28px] border border-stone-200/80 p-6 sm:p-10 shadow-[0_1px_2px_rgba(24,53,47,0.04),0_16px_40px_-28px_rgba(24,53,47,0.18)]";
export const LABEL = "text-xs font-semibold uppercase tracking-[0.12em]";
export const H2 = "font-display text-2xl sm:text-3xl font-bold text-[hsl(var(--forest))] leading-tight tracking-tight";
const EYEBROW = "font-mono text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]";

const MONTHS_SHORT = ["jan.", "fev.", "mar.", "abr.", "mai.", "jun.", "jul.", "ago.", "set.", "out.", "nov.", "dez."];

export function formatShortPT(value) {
    if (!value) return "";
    const s = String(value);
    const dt = s.length > 10 ? new Date(s) : null;
    const [y, m, d] = dt && !isNaN(dt)
        ? [dt.getFullYear(), dt.getMonth() + 1, dt.getDate()]
        : s.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return s;
    return `${d} ${MONTHS_SHORT[m - 1]} ${y}`;
}

const STEP_ICONS = { 1: Inbox, 2: FileText, 3: FileCheck, 4: Handshake, 5: Hammer, 6: Trophy };
const STEP_TINTS = {
    1: "bg-stone-100 text-stone-700 ring-stone-200",
    2: "bg-[hsl(var(--copper))]/10 text-[hsl(var(--copper))] ring-[hsl(var(--copper))]/30",
    3: "bg-blue-50 text-blue-700 ring-blue-200",
    4: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    5: "bg-orange-50 text-[hsl(var(--copper))] ring-orange-200",
    6: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};
const BADGE_TEXT = { "Adjudicado": "Adjudicado", "Concluído": "Concluído" };

export function ClientIntro({ name }) {
    const parts = (name || "").trim().split(/\s+/).filter(Boolean);
    const first = parts[0] || "";
    const initials = parts.length ? (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase() : "";
    return (
        <div className="flex items-start justify-between gap-4" data-testid="client-intro">
            <div className="min-w-0">
                <div className={EYEBROW}>O seu pedido</div>
                <h1 className="font-display text-[2.5rem] sm:text-5xl font-bold text-[hsl(var(--forest))] tracking-tight leading-[1.05] mt-2" data-testid="client-greeting">
                    Olá{first ? `, ${first}` : ""}.
                </h1>
                <p className="text-stone-600 text-base sm:text-lg leading-relaxed mt-3 max-w-md" data-testid="client-intro-text">
                    Aqui encontra toda a informação sobre a sua obra, sempre atualizada.
                </p>
            </div>
            {initials && (
                <div aria-hidden className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-emerald-50 ring-1 ring-emerald-100 text-[hsl(var(--forest))] font-display font-bold text-base sm:text-lg flex items-center justify-center">
                    {initials}
                </div>
            )}
        </div>
    );
}

function heroPrimary(d) {
    const e = d.execution || {};
    if (d.status === "Concluído") {
        const done = formatShortPT(e.completed_at);
        return done
            ? { testid: "client-completed", label: "Obra concluída", value: done, isDate: true, note: "Obrigado pela confiança.", noteIcon: Trophy }
            : { testid: "client-completed", value: "Obrigado pela confiança." };
    }
    if (d.status === "Execução") {
        const exp = formatShortPT(e.expected_completion_date);
        return exp
            ? { testid: "client-execution-active", label: "Conclusão prevista", value: exp, isDate: true, valueTestid: "client-expected-completion", note: "A sua obra está a avançar.", noteIcon: BarChart3 }
            : { testid: "client-execution-active", value: "A sua obra está a avançar." };
    }
    if (d.status === "Adjudicado") {
        const start = formatShortPT(e.date);
        return start
            ? { testid: "client-execution-planned", label: "Execução prevista", value: start, isDate: true }
            : { testid: "client-execution-tbc", label: "Execução", value: "Data a confirmar." };
    }
    const delivery = formatShortPT(d.proposal_delivery_date);
    if (delivery) return { testid: "client-delivery-date", label: "Entrega prevista", value: delivery, isDate: true };
    return { testid: "client-primary", label: "Estado atual", value: d.timeline_label };
}

export function ClientHero({ d, photoId }) {
    const StepIcon = STEP_ICONS[d.timeline_step] || Inbox;
    const tint = STEP_TINTS[d.timeline_step] || STEP_TINTS[1];
    const workTypes = Array.isArray(d.work_types) ? d.work_types.filter(Boolean) : [];
    const pr = heroPrimary(d);
    const NoteIcon = pr.noteIcon;
    const hasMeta = workTypes.length > 0 || !!d.location;
    const showNext = d.status !== "Execução" && d.status !== "Concluído" && d.next_step_text;
    return (
        <section className="bg-white rounded-[28px] border border-stone-200/80 p-5 sm:p-8 lg:p-10 shadow-[0_1px_2px_rgba(24,53,47,0.04),0_16px_40px_-24px_rgba(24,53,47,0.2)]" data-testid="client-hero">
            <div className={photoId ? "lg:grid lg:grid-cols-[1.2fr_1fr] lg:gap-10 lg:items-center" : ""}>
                <div className="min-w-0">
                    <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 ring-1 ${tint}`} data-testid="client-status-badge">
                        <StepIcon className="w-4 h-4" aria-hidden />
                        <span className="text-[13px] font-semibold uppercase tracking-wide" data-testid="client-status">
                            {BADGE_TEXT[d.status] || d.timeline_label}
                        </span>
                    </div>
                    {workTypes.length > 0 && <div className={`${EYEBROW} mt-6`} data-testid="client-work-type">{workTypes[0]}</div>}
                    <div className={workTypes.length > 0 ? "mt-2" : "mt-6"} data-testid={pr.testid}>
                        {pr.isDate ? (
                            <>
                                <div className="font-display text-[1.6rem] sm:text-4xl font-bold text-[hsl(var(--forest))] tracking-tight leading-tight">{pr.label}</div>
                                <div className="font-display text-[2.6rem] sm:text-6xl font-bold text-[hsl(var(--forest))] tracking-tight leading-[1.05]" data-testid={pr.valueTestid}>{pr.value}</div>
                            </>
                        ) : (
                            <>
                                {pr.label && <div className={`${LABEL} text-stone-500 mb-1.5`}>{pr.label}</div>}
                                <div className="font-display text-[1.9rem] sm:text-5xl font-bold text-[hsl(var(--forest))] tracking-tight leading-[1.08]">{pr.value}</div>
                            </>
                        )}
                    </div>
                    {(hasMeta || pr.note) && (
                        <div className="mt-6 pt-5 border-t border-stone-100 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-6 text-[15px] sm:text-base text-stone-700" data-testid="client-work-info">
                            {hasMeta && (
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                    {workTypes.length > 0 && (
                                        <span className="inline-flex items-start gap-2 min-w-0" data-testid="client-work-types"><Wrench className="w-4 h-4 mt-[3px] flex-shrink-0 text-[hsl(var(--copper))]" aria-hidden /><span className="min-w-0 break-words">{workTypes.join(" · ")}</span></span>
                                    )}
                                    {d.location && (
                                        <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-[hsl(var(--copper))]" aria-hidden />{d.location}</span>
                                    )}
                                </div>
                            )}
                            {pr.note && (
                                <span className="inline-flex items-center gap-2" data-testid="client-progress-phrase">
                                    <NoteIcon className="w-4 h-4 text-[hsl(var(--copper))]" aria-hidden />{pr.note}
                                </span>
                            )}
                        </div>
                    )}
                    {showNext && (
                        <p className="mt-5 text-stone-700 leading-relaxed text-base max-w-2xl" data-testid="client-next-step">{d.next_step_text}</p>
                    )}
                </div>
                {photoId && (
                    <div className="mt-6 lg:mt-0 w-full aspect-video lg:aspect-[4/3] rounded-2xl overflow-hidden bg-stone-100">
                        <img
                            src={`/api/files/${photoId}`}
                            alt="Fotografia da obra"
                            fetchPriority="high"
                            decoding="async"
                            className="w-full h-full object-cover"
                            data-testid="client-hero-photo"
                        />
                    </div>
                )}
            </div>
            <RequestDetails text={d.description} />
        </section>
    );
}

export function ClientNav({ items, active, onSelect }) {
    return (
        <nav aria-label="Secções da Área do Cliente" data-testid="client-nav">
            <ul className="grid gap-2.5 lg:flex lg:gap-3">
                {items.map((it) => {
                    const on = it.key === active;
                    const Icon = it.icon;
                    return (
                        <li key={it.key} className="lg:flex-auto">
                            <button
                                type="button"
                                onClick={() => onSelect(it.key)}
                                aria-current={on ? "true" : undefined}
                                className={`w-full min-h-[58px] flex items-center gap-4 lg:justify-center lg:gap-2.5 px-5 py-4 lg:py-3.5 rounded-2xl text-left text-base lg:text-[15px] transition-colors ${on
                                    ? "bg-emerald-50 text-[hsl(var(--forest))] font-semibold ring-1 ring-emerald-200/80 lg:bg-white lg:ring-stone-200 lg:shadow-[inset_0_-3px_0_hsl(var(--forest))]"
                                    : "bg-stone-200/40 text-stone-700 hover:bg-stone-200/70"}`}
                                data-testid={`client-nav-${it.key}`}
                            >
                                <Icon className="w-5 h-5 flex-shrink-0" aria-hidden />
                                <span className="flex-1 lg:flex-initial lg:whitespace-nowrap">{it.label}</span>
                                {it.badge > 0 && (
                                    <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[hsl(var(--copper))] text-white text-xs font-bold flex items-center justify-center" aria-label={it.badgeLabel || `${it.badge} em aberto`} data-testid={`client-nav-${it.key}-badge`}>
                                        {it.badge}
                                    </span>
                                )}
                                <ChevronRight className="w-5 h-5 text-stone-400 lg:hidden" aria-hidden />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}

function RequestDetails({ text }) {
    const [open, setOpen] = useState(false);
    if (!text || !text.trim()) return null;
    return (
        <div className="mt-5 border-t border-stone-100" data-testid="client-request-details">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-controls="client-request-details-panel"
                className="w-full min-h-[56px] flex items-center justify-between gap-4 py-3 text-left"
                data-testid="client-request-details-toggle"
            >
                <span className="font-display text-base sm:text-lg font-bold text-[hsl(var(--forest))]">Detalhes do pedido</span>
                <span className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[hsl(var(--copper))] flex-shrink-0">
                    {open ? "Fechar detalhes" : "Ver detalhes"}
                    <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
                </span>
            </button>
            {open && (
                <div id="client-request-details-panel" className="pb-1" data-testid="client-request-details-panel">
                    <div className={`${LABEL} text-stone-500 mb-2`}>Descrição do pedido</div>
                    <p className="text-stone-800 text-base leading-relaxed whitespace-pre-wrap break-words max-w-3xl" data-testid="client-request-description">{text}</p>
                </div>
            )}
        </div>
    );
}

const TONES = {
    green: { card: "bg-emerald-50/50 border-emerald-100", icon: "bg-emerald-100/70 text-emerald-800", cta: "text-emerald-800" },
    copper: { card: "bg-orange-50/60 border-orange-100", icon: "bg-orange-100/70 text-[hsl(var(--copper))]", cta: "text-[hsl(var(--copper))]" },
    slate: { card: "bg-slate-50/80 border-slate-200/70", icon: "bg-slate-100 text-slate-700", cta: "text-slate-700" },
};
const CARD_COLS = { 2: "sm:grid-cols-2", 3: "md:grid-cols-3" };

export function OverviewCards({ cards }) {
    if (!cards.length) return null;
    return (
        <div className={`grid gap-3 sm:gap-4 ${CARD_COLS[cards.length] || ""}`} data-testid="client-overview-cards">
            {cards.map((c) => {
                const t = TONES[c.tone];
                const Icon = c.icon;
                return (
                    <button
                        key={c.key}
                        type="button"
                        onClick={c.onClick}
                        className={`group text-left flex items-start gap-4 rounded-2xl border p-5 sm:p-6 transition-shadow hover:shadow-[0_10px_28px_-18px_rgba(24,53,47,0.35)] ${t.card}`}
                        data-testid={`overview-${c.key}`}
                    >
                        <span className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${t.icon}`}>
                            <Icon className="w-5 h-5" aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block font-display text-lg font-bold text-stone-900 leading-snug">{c.title}</span>
                            <span className="block text-stone-600 text-[15px] mt-0.5" data-testid={`overview-${c.key}-value`}>{c.value}</span>
                            <span className={`mt-3 inline-flex items-center gap-1.5 text-[15px] font-semibold ${t.cta}`}>
                                {c.cta}
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export function ContactsPanel({ settings }) {
    const rows = [
        settings.phone && { key: "phone", icon: Phone, label: "Telefone", value: settings.phone, href: `tel:${settings.phone.replace(/\s+/g, "")}` },
        settings.email && { key: "email", icon: Mail, label: "Email", value: settings.email, href: `mailto:${settings.email}` },
        settings.hours && { key: "hours", icon: Clock, label: "Horário", value: settings.hours },
    ].filter(Boolean);
    return (
        <section className={CARD} data-testid="client-contacts-section">
            <div className="max-w-2xl">
                <h2 className={H2}>Contactos</h2>
                <p className="text-stone-600 text-base leading-relaxed mt-2">Se precisar de falar connosco, estamos disponíveis pelos contactos abaixo.</p>
                <div className="mt-6 space-y-3">
                    {rows.map((r) => {
                        const Icon = r.icon;
                        const inner = (
                            <>
                                <span className="flex-shrink-0 w-11 h-11 rounded-full bg-emerald-50 text-[hsl(var(--forest))] flex items-center justify-center">
                                    <Icon className="w-5 h-5" aria-hidden />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm text-stone-500">{r.label}</span>
                                    <span className="block text-base font-semibold text-stone-900 break-words">{r.value}</span>
                                </span>
                                {r.href && <ChevronRight className="w-5 h-5 text-stone-400 flex-shrink-0" aria-hidden />}
                            </>
                        );
                        const cls = "flex items-center gap-4 rounded-2xl border border-stone-200 p-4 min-h-[68px]";
                        return r.href ? (
                            <a key={r.key} href={r.href} className={`${cls} hover:border-[hsl(var(--copper))] transition-colors`} data-testid={`client-contact-${r.key}`}>{inner}</a>
                        ) : (
                            <div key={r.key} className={cls} data-testid={`client-contact-${r.key}`}>{inner}</div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
